import { parseSkillBlocks, type SkillBlock, type SkillHeadingBlock } from './skillBlocks';

/** One heading of a skill's outline: what the panel lists before anyone opens the file itself. */
export interface SkillOutlineEntry {
  /** 2 for a section, 3 for a step inside one. */
  level: number;
  text: string;
  /** Anchor id, taken from the rendered heading so the jump list always lands. */
  id: string;
  /** The number the author typed, or null. Never invented: most skills do not number their steps. */
  number: string | null;
}

/** A SKILL.md split into the metadata the panel labels it with and the outline it leads on. */
export interface ParsedSkillManifest {
  name: string | null;
  description: string | null;
  version: string | null;
  author: string | null;
  blocks: SkillBlock[];
  outline: SkillOutlineEntry[];
}

const FRONT_MATTER = /^---\n([\s\S]*?)\n---\n?/;

/** Reads one scalar off the front matter, unquoting it and folding YAML's indented continuations. */
function frontMatterValue(block: string, key: string): string | null {
  const lines = block.split('\n');
  const index = lines.findIndex((line) => line.startsWith(`${key}:`));
  if (index === -1) {
    return null;
  }

  let value = (lines[index] ?? '').slice(key.length + 1).trim();
  for (let i = index + 1; i < lines.length; i += 1) {
    const next = lines[i] ?? '';
    if (!/^\s+\S/.test(next) || /^\s*[a-zA-Z_]+:/.test(next)) {
      break;
    }
    value += ` ${next.trim()}`;
  }
  const unquoted = value.replace(/^["']|["']$/g, '').trim();
  return unquoted.length > 0 ? unquoted : null;
}

/**
 * Parses a SKILL.md into its front matter and the outline the panel leads with. The outline is taken
 * from the same blocks the body renders from, so an entry's anchor is always the id of the heading it
 * points at; deriving it separately once let a leading H1 shift the two counters apart.
 *
 * The outline is derived rather than summarised: the headings an agent wrote are the closest thing to
 * a truthful answer to "what does this skill do".
 */
export function parseSkillManifest(source: string): ParsedSkillManifest {
  const match = source.match(FRONT_MATTER);
  const frontMatter = match?.[1] ?? '';
  const body = match ? source.slice(match[0].length) : source;
  const blocks = parseSkillBlocks(body);

  const outline: SkillOutlineEntry[] = blocks
    .filter((block) => block.kind === 'heading' && (block.level === 2 || block.level === 3))
    .map((block) => {
      const heading = block as SkillHeadingBlock;
      return { level: heading.level, text: heading.text, id: heading.id, number: heading.number };
    });

  return {
    name: frontMatterValue(frontMatter, 'name'),
    description: frontMatterValue(frontMatter, 'description'),
    version: frontMatterValue(frontMatter, 'version'),
    author: frontMatterValue(frontMatter, 'author'),
    blocks,
    outline,
  };
}
